import * as React from 'react'
import { CalendarIcon, XIcon } from 'lucide-react'

import { cn } from '@/shared/lib/utils'

import { buttonVariants } from './button'
import { Calendar } from './calendar'
import {
	DateFieldDays,
	DateFieldMonths,
	DateFieldSeparator,
	DateFieldYears,
} from './date-field'
import * as DatePickerPrimitive from './date-picker-primitive'
import {
	InputBase,
	InputBaseAdornment,
	InputBaseAdornmentButton,
	InputBaseFlexWrapper,
} from './input-base'

function DatePicker(
	props: React.ComponentProps<typeof DatePickerPrimitive.Root>
) {
	return <DatePickerPrimitive.Root data-slot='date-picker' {...props} />
}

function DatePickerAnchor(
	props: React.ComponentProps<typeof DatePickerPrimitive.Anchor>
) {
	return (
		<DatePickerPrimitive.Anchor data-slot='date-picker-anchor' {...props} />
	)
}

function DatePickerInputBase({
	children,
	...props
}: React.ComponentProps<typeof InputBase>) {
	const { required } = DatePickerPrimitive.useDatePicker()

	return (
		<DatePickerPrimitive.Anchor asChild>
			<InputBase data-slot='date-picker-input-base' {...props}>
				<InputBaseFlexWrapper>{children}</InputBaseFlexWrapper>
				{!required && (
					<InputBaseAdornment>
						<InputBaseAdornmentButton asChild>
							<DatePickerPrimitive.Clear>
								<span className='sr-only'>Clear date</span>
								<XIcon />
							</DatePickerPrimitive.Clear>
						</InputBaseAdornmentButton>
					</InputBaseAdornment>
				)}
				<InputBaseAdornment>
					<InputBaseAdornmentButton asChild>
						<DatePickerPrimitive.Trigger>
							<CalendarIcon />
						</DatePickerPrimitive.Trigger>
					</InputBaseAdornmentButton>
				</InputBaseAdornment>
			</InputBase>
		</DatePickerPrimitive.Anchor>
	)
}

function DatePickerDateRangeField({
	disabled: disabledProp,
	format = 'EU',
	className,
	...props
}: React.ComponentProps<typeof DatePickerPrimitive.DateRangeField> & {
	format: 'EU' | 'US'
}) {
	const { disabled } = DatePickerPrimitive.useDatePicker()

	return (
		<DatePickerPrimitive.DateRangeField
			data-slot='date-picker-date-range-field'
			disabled={disabled || disabledProp}
			className={cn('flex gap-1.5', className)}
			{...props}
		>
			{format === 'EU' ? (
				<DatePickerPrimitive.DateRangeFieldFrom className='flex items-center'>
					<DatePickerPrimitive.DateRangeFieldDays asChild>
						<DateFieldDays />
					</DatePickerPrimitive.DateRangeFieldDays>
					<DatePickerPrimitive.DateRangeFieldSeparator asChild>
						<DateFieldSeparator />
					</DatePickerPrimitive.DateRangeFieldSeparator>
					<DatePickerPrimitive.DateRangeFieldMonths asChild>
						<DateFieldMonths />
					</DatePickerPrimitive.DateRangeFieldMonths>
					<DatePickerPrimitive.DateRangeFieldSeparator asChild>
						<DateFieldSeparator />
					</DatePickerPrimitive.DateRangeFieldSeparator>
					<DatePickerPrimitive.DateRangeFieldYears asChild>
						<DateFieldYears />
					</DatePickerPrimitive.DateRangeFieldYears>
				</DatePickerPrimitive.DateRangeFieldFrom>
			) : (
				<DatePickerPrimitive.DateRangeFieldFrom className='flex items-center'>
					<DatePickerPrimitive.DateRangeFieldMonths asChild>
						<DateFieldMonths />
					</DatePickerPrimitive.DateRangeFieldMonths>
					<DatePickerPrimitive.DateRangeFieldSeparator asChild>
						<DateFieldSeparator />
					</DatePickerPrimitive.DateRangeFieldSeparator>
					<DatePickerPrimitive.DateRangeFieldDays asChild>
						<DateFieldDays />
					</DatePickerPrimitive.DateRangeFieldDays>
					<DatePickerPrimitive.DateRangeFieldSeparator asChild>
						<DateFieldSeparator />
					</DatePickerPrimitive.DateRangeFieldSeparator>
					<DatePickerPrimitive.DateRangeFieldYears asChild>
						<DateFieldYears />
					</DatePickerPrimitive.DateRangeFieldYears>
				</DatePickerPrimitive.DateRangeFieldFrom>
			)}

			<DatePickerPrimitive.DateRangeFieldSeparator>
				-
			</DatePickerPrimitive.DateRangeFieldSeparator>

			{format === 'EU' ? (
				<DatePickerPrimitive.DateRangeFieldTo className='flex items-center'>
					<DatePickerPrimitive.DateRangeFieldDays asChild>
						<DateFieldDays />
					</DatePickerPrimitive.DateRangeFieldDays>
					<DatePickerPrimitive.DateRangeFieldSeparator asChild>
						<DateFieldSeparator />
					</DatePickerPrimitive.DateRangeFieldSeparator>
					<DatePickerPrimitive.DateRangeFieldMonths asChild>
						<DateFieldMonths />
					</DatePickerPrimitive.DateRangeFieldMonths>
					<DatePickerPrimitive.DateRangeFieldSeparator asChild>
						<DateFieldSeparator />
					</DatePickerPrimitive.DateRangeFieldSeparator>
					<DatePickerPrimitive.DateRangeFieldYears asChild>
						<DateFieldYears />
					</DatePickerPrimitive.DateRangeFieldYears>
				</DatePickerPrimitive.DateRangeFieldTo>
			) : (
				<DatePickerPrimitive.DateRangeFieldTo className='flex items-center'>
					<DatePickerPrimitive.DateRangeFieldMonths asChild>
						<DateFieldMonths />
					</DatePickerPrimitive.DateRangeFieldMonths>
					<DatePickerPrimitive.DateRangeFieldSeparator asChild>
						<DateFieldSeparator />
					</DatePickerPrimitive.DateRangeFieldSeparator>
					<DatePickerPrimitive.DateRangeFieldDays asChild>
						<DateFieldDays />
					</DatePickerPrimitive.DateRangeFieldDays>
					<DatePickerPrimitive.DateRangeFieldSeparator asChild>
						<DateFieldSeparator />
					</DatePickerPrimitive.DateRangeFieldSeparator>
					<DatePickerPrimitive.DateRangeFieldYears asChild>
						<DateFieldYears />
					</DatePickerPrimitive.DateRangeFieldYears>
				</DatePickerPrimitive.DateRangeFieldTo>
			)}
		</DatePickerPrimitive.DateRangeField>
	)
}

function DatePickerDateField({
	format = 'EU',
	disabled: disabledProp,
	className,
	...props
}: React.ComponentProps<typeof DatePickerPrimitive.DateField> & {
	format: 'EU' | 'US'
}) {
	const { disabled } = DatePickerPrimitive.useDatePicker()

	return (
		<DatePickerPrimitive.DateField
			data-slot='date-picker-date-field'
			disabled={disabled || disabledProp}
			className={cn('flex', className)}
			{...props}
		>
			{format === 'EU' ? (
				<>
					<DatePickerPrimitive.DateFieldDays asChild>
						<DateFieldDays />
					</DatePickerPrimitive.DateFieldDays>
					<DatePickerPrimitive.DateFieldSeparator asChild>
						<DateFieldSeparator />
					</DatePickerPrimitive.DateFieldSeparator>
					<DatePickerPrimitive.DateFieldMonths asChild>
						<DateFieldMonths />
					</DatePickerPrimitive.DateFieldMonths>
					<DatePickerPrimitive.DateFieldSeparator asChild>
						<DateFieldSeparator />
					</DatePickerPrimitive.DateFieldSeparator>
					<DatePickerPrimitive.DateFieldYears asChild>
						<DateFieldYears />
					</DatePickerPrimitive.DateFieldYears>
				</>
			) : (
				<>
					<DatePickerPrimitive.DateFieldMonths asChild>
						<DateFieldMonths />
					</DatePickerPrimitive.DateFieldMonths>
					<DatePickerPrimitive.DateFieldSeparator asChild>
						<DateFieldSeparator />
					</DatePickerPrimitive.DateFieldSeparator>
					<DatePickerPrimitive.DateFieldDays asChild>
						<DateFieldDays />
					</DatePickerPrimitive.DateFieldDays>
					<DatePickerPrimitive.DateFieldSeparator asChild>
						<DateFieldSeparator />
					</DatePickerPrimitive.DateFieldSeparator>
					<DatePickerPrimitive.DateFieldYears asChild>
						<DateFieldYears />
					</DatePickerPrimitive.DateFieldYears>
				</>
			)}
		</DatePickerPrimitive.DateField>
	)
}

function DatePickerInput(
	props: React.ComponentProps<
		typeof DatePickerDateField | typeof DatePickerDateRangeField
	>
) {
	const { mode, disabled, invalid } = DatePickerPrimitive.useDatePicker()

	return (
		<DatePickerInputBase disabled={disabled} aria-invalid={invalid}>
			{mode === 'range' ? (
				<DatePickerDateRangeField
					{...(props as React.ComponentProps<typeof DatePickerDateRangeField>)}
				/>
			) : (
				<DatePickerDateField
					{...(props as React.ComponentProps<typeof DatePickerDateField>)}
				/>
			)}
		</DatePickerInputBase>
	)
}

function DatePickerTrigger({
	children,
	className,
	...props
}: React.ComponentProps<typeof DatePickerPrimitive.Trigger>) {
	return (
		<InputBase
			data-slot='date-picker-trigger'
			asChild
			className={cn(
				buttonVariants({ variant: 'outline' }),
				'cursor-pointer font-normal',
				className
			)}
		>
			<DatePickerPrimitive.Trigger {...props}>
				<InputBaseAdornment>
					<CalendarIcon />
				</InputBaseAdornment>
				<InputBaseFlexWrapper>{children}</InputBaseFlexWrapper>
			</DatePickerPrimitive.Trigger>
		</InputBase>
	)
}

function DatePickerValue({
	className,
	...props
}: React.ComponentProps<typeof DatePickerPrimitive.Value>) {
	return (
		<DatePickerPrimitive.Value
			data-slot='date-picker-value'
			className={cn('data-placeholder:text-muted-foreground/40', className)}
			{...props}
		/>
	)
}

function DatePickerContent({
	className,
	align = 'start',
	alignOffset = 4,
	...props
}: React.ComponentProps<typeof DatePickerPrimitive.Content>) {
	return (
		<DatePickerPrimitive.Portal>
			<DatePickerPrimitive.Content
				data-slot='date-picker-content'
				align={align}
				alignOffset={alignOffset}
				className={cn(
					'relative z-50 max-h-96 w-auto overflow-hidden rounded-lg border bg-popover p-0 text-popover-foreground shadow-md data-[side=bottom]:translate-y-1 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:-translate-x-1 data-[side=left]:slide-in-from-right-2 data-[side=right]:translate-x-1 data-[side=right]:slide-in-from-left-2 data-[side=top]:-translate-y-1 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
					className
				)}
				{...props}
			/>
		</DatePickerPrimitive.Portal>
	)
}

function DatePickerCalendar(props: React.ComponentProps<typeof Calendar>) {
	return (
		<DatePickerPrimitive.Calendar data-slot='date-picker-calendar' asChild>
			<Calendar {...props} />
		</DatePickerPrimitive.Calendar>
	)
}

export {
	DatePicker,
	DatePickerAnchor,
	DatePickerInput,
	DatePickerTrigger,
	DatePickerValue,
	DatePickerContent,
	DatePickerCalendar,
}
